import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ReactNode } from 'react';
import { type VariantProps, tv } from 'tailwind-variants';

import ChevronRightIcon from '@cartwhl/ui/icons/ChevronRightIcon';

import Text from '../base/Text';
import './Dropdown.css';
import Tooltip from './Tooltip';

const DEFAULT_DROPDOWN_Z_INDEX = 20;
const MODAL_DROPDOWN_Z_INDEX = 10000;

const dropdownContent = tv({
  base: 'RxDropdownContent shadow-menu rounded-2xl',
  variants: {
    variant: {
      menu: 'text-gray-90 dark:bg-gray-75 bg-white p-2 dark:text-white',
      panel:
        'border-primary bg-primary text-primary dark:bg-gray-90 border py-2 dark:text-white',
    },
  },
  defaultVariants: {
    variant: 'menu',
  },
});

export default function Dropdown({
  trigger,
  children,
  open,
  onOpenChange,
  side,
  align = 'end',
  disabled,
  width,
  inModal = false,
  zIndex,
  sideOffset = 8,
  triggerTooltip,
  triggerTooltipDelayDuration,
  triggerTooltipSide = 'top',
  contentVariant,
  onCloseAutoFocus,
}: {
  trigger: ReactNode;
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  disabled?: boolean;
  width?: number;
  inModal?: boolean;
  zIndex?: number;
  sideOffset?: number;
  triggerTooltip?: ReactNode;
  triggerTooltipDelayDuration?: number;
  triggerTooltipSide?: 'top' | 'right' | 'bottom' | 'left';
  contentVariant?: VariantProps<typeof dropdownContent>['variant'];
  onCloseAutoFocus?: (event: Event) => void;
}) {
  const dropdownZIndex =
    zIndex ?? (inModal ? MODAL_DROPDOWN_Z_INDEX : DEFAULT_DROPDOWN_Z_INDEX);

  const triggerNode = (
    <DropdownMenu.Trigger asChild disabled={disabled}>
      {trigger}
    </DropdownMenu.Trigger>
  );

  return (
    <DropdownMenu.Root open={open} onOpenChange={onOpenChange} modal={false}>
      {triggerTooltip != null ? (
        <Tooltip
          text={triggerTooltip}
          side={triggerTooltipSide}
          inModal={inModal}
          disabled={disabled}
          delayDuration={triggerTooltipDelayDuration}
        >
          {triggerNode}
        </Tooltip>
      ) : (
        triggerNode
      )}
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          side={side}
          className={dropdownContent({ variant: contentVariant })}
          align={align}
          collisionPadding={8}
          onCloseAutoFocus={onCloseAutoFocus}
          sideOffset={sideOffset}
          style={{ width, zIndex: dropdownZIndex }}
        >
          <Text as="div" variant="studio/body/medium/sm">
            {children}
          </Text>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

const menuRowIconCellClass =
  'flex h-5 w-5 shrink-0 items-center justify-center justify-self-center';

const itemInner = tv({
  base: 'group grid w-full min-w-[260px] grid-cols-[20px_minmax(0,1fr)] items-center gap-x-3 rounded-lg px-4 py-2.5 transition-colors dark:text-white',
  variants: {
    variant: {
      default: '',
      blue: 'bg-blue/10',
    },
    disabled: {
      true: 'cursor-default opacity-50',
      false: 'cursor-pointer',
    },
    justify: {
      start: 'justify-start',
      center: 'justify-center',
      end: 'justify-end',
    },
  },
  compoundVariants: [
    {
      variant: 'default',
      disabled: false,
      className: 'hover:bg-gray-15 dark:hover:bg-gray-90',
    },
    {
      variant: 'blue',
      disabled: false,
      className:
        'hover:bg-blue dark:hover:bg-blue hover:text-white dark:hover:text-white',
    },
  ],
  defaultVariants: {
    variant: 'default',
    disabled: false,
  },
});
Dropdown.Item = function Item({
  children,
  href,
  onClick,
  icon,
  disabled = false,
  variant,
  className,
  itemClassName,
  textClassName,
  justify,
}: {
  children: ReactNode;
  href?: string;
  onClick?: () => void;
  icon?: ReactNode;
  disabled?: boolean;
  variant?: VariantProps<typeof itemInner>['variant'];
  justify?: VariantProps<typeof itemInner>['justify'];
  className?: string;
  itemClassName?: string;
  textClassName?: string;
}) {
  const path = href?.split('?')[0];
  const isExternal = path && !path.includes('.getcartwheel.com');
  const itemClassNames = `${itemInner({
    variant,
    disabled,
    justify,
    className: itemClassName,
  })} outline-hidden overflow-hidden ${className ?? ''}`;
  const content = (
    <>
      <span className={menuRowIconCellClass}>{icon ?? null}</span>
      <Text
        variant="studio/body/medium/sm"
        className={`min-w-0 text-left ${textClassName ?? ''}`}
      >
        {children}
      </Text>
    </>
  );

  if (href) {
    return (
      <DropdownMenu.Item
        asChild
        disabled={disabled}
        onSelect={() => onClick?.()}
      >
        <a
          href={href}
          target={isExternal ? '_blank' : undefined}
          rel={isExternal ? 'noreferrer noopener' : undefined}
          aria-disabled={disabled || undefined}
          tabIndex={disabled ? -1 : undefined}
          className={itemClassNames}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            if (disabled) {
              event.preventDefault();
            }
          }}
        >
          {content}
        </a>
      </DropdownMenu.Item>
    );
  }

  return (
    <DropdownMenu.Item
      className={itemClassNames}
      onSelect={() => onClick?.()}
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
      disabled={disabled}
    >
      {content}
    </DropdownMenu.Item>
  );
};

Dropdown.Sub = function Sub({
  children,
  text,
  icon,
  suffix,
  zIndex,
  contentClassName,
  textClassName,
}: {
  children: ReactNode;
  text: string;
  icon?: ReactNode | null | undefined; // Null means: empty icon. Undefined means no icon.
  /** Rendered between the label and the chevron (e.g. a “New” pill). */
  suffix?: ReactNode;
  zIndex?: number;
  /** Classes for the inner flyout body (width, padding, etc.). */
  contentClassName?: string;
  /** Classes for the row label text only. */
  textClassName?: string;
}) {
  return (
    <DropdownMenu.Sub>
      <DropdownMenu.SubTrigger className="dark:hover:bg-gray-90 hover:bg-gray-15 data-[state=open]:bg-gray-15 dark:data-[state=open]:bg-gray-90 outline-hidden grid w-full min-w-[260px] cursor-default grid-cols-[20px_minmax(0,1fr)_auto] items-center gap-x-3 rounded-lg px-4 py-2.5 transition-colors">
        <span className={menuRowIconCellClass}>{icon ?? null}</span>
        <Text
          variant="studio/body/medium/sm"
          className={`min-w-0 truncate text-left ${textClassName ?? ''}`}
        >
          {text}
        </Text>
        <span className="flex shrink-0 items-center gap-2 justify-self-end">
          {suffix != null ? suffix : null}
          <ChevronRightIcon className="text-gray-45 shrink-0" />
        </span>
      </DropdownMenu.SubTrigger>
      <DropdownMenu.Portal>
        <DropdownMenu.SubContent
          className="RxSubContent text-gray-90 dark:bg-gray-75 shadow-menu mr-[-150px] rounded-2xl bg-white p-2 sm:mr-0 dark:text-white"
          style={{ zIndex: zIndex ?? DEFAULT_DROPDOWN_Z_INDEX }}
        >
          <div
            className={
              contentClassName ??
              'flex min-w-0 flex-col gap-0 text-[14px]/[18px] font-medium'
            }
          >
            {children}
          </div>
        </DropdownMenu.SubContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Sub>
  );
};

Dropdown.Separator = function Separator() {
  return (
    <DropdownMenu.Separator className="bg-gray-30 dark:bg-gray-60 -mx-2 my-2 h-px" />
  );
};
